'use client'

import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from 'lucide-react'
import { toast, Toaster as Sonner, type ToasterProps } from 'sonner'

export const DEFAULT_TOAST_DURATION_MS = 3000

export function getToasterSwipeDirections(_isMobile: boolean): NonNullable<ToasterProps['swipeDirections']> {
  return ['right']
}

export function getToasterOffset(isMobile: boolean): NonNullable<ToasterProps['offset']> {
  return isMobile ? 64 : { top: 72, right: 16 }
}

const Toaster = ({ theme = 'system', duration = DEFAULT_TOAST_DURATION_MS, ...props }: ToasterProps) => {
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position={isMobile ? 'top-center' : 'top-right'}
      offset={getToasterOffset(isMobile)}
      gap={6}
      expand
      visibleToasts={5}
      duration={duration}
      swipeDirections={getToasterSwipeDirections(isMobile)}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          // Read the Tailwind theme variable (`--color-popover`) rather than
          // the source token (`--popover`). Apps store `--popover` in three
          // different formats — raw HSL triplet (`0 0% 100%`), pre-wrapped
          // (`hsl(0 0% 100%)`, e.g. HQ via `@ordo/ui/theme`), or hex
          // (`#ffffff`, e.g. factory-scanner). `--color-popover` is wrapped
          // into a valid CSS color at every app's `@theme inline` boundary,
          // so reading it is format-agnostic. The previous `hsl(var(--popover))`
          // produced invalid `hsl(hsl(...))` / `hsl(#...)` on the latter
          // two formats and rendered the toast fully transparent.
          '--normal-bg': 'var(--color-popover)',
          '--normal-text': 'var(--color-popover-foreground)',
          '--normal-border': 'var(--color-border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          // Scope the taller/shadow chrome to Sonner's styled toasts only.
          // Custom toasts (toast.custom(..., { unstyled: true }) — i.e. every
          // ActionToast surface) render with data-styled="false"; applying
          // min-h/py-4/shadow there stretches the <li> taller than the inner
          // card and double-stacks shadows, producing a visible ghost shell.
          toast: 'data-[styled=true]:min-h-[60px] data-[styled=true]:py-4 data-[styled=true]:shadow-md',
          actionButton:
            '!bg-muted !text-foreground !shadow-none !ring-1 !ring-border transition-colors hover:!bg-accent hover:!text-accent-foreground',
          cancelButton:
            '!bg-transparent !text-muted-foreground !shadow-none !ring-1 !ring-border transition-colors hover:!bg-muted hover:!text-foreground',
        },
      }}
      {...props}
    />
  )
}

export { toast, Toaster, type ToasterProps }
